/**
 * useNotifications.ts
 *
 * Manages notification state for the current user:
 *   - Polls /notifications/unread-count every 30 s when authenticated
 *   - Fetches the paginated (grouped) list on demand
 *   - markRead / markGroupRead / markAllRead with optimistic updates + rollback
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  fetchNotifications,
  fetchUnreadCount,
  markNotificationRead,
  markGroupRead as apiMarkGroupRead,
  markAllNotificationsRead,
  type Notification,
  type NotificationsPage,
} from '../services/api/notificationApi';

const POLL_INTERVAL_MS = 30_000;

export interface UseNotificationsState {
  unreadCount: number;
  notifications: Notification[];
  total: number;
  currentPage: number;
  hasMore: boolean;
  listLoading: boolean;
  listError: string | null;
  markRead: (id: number, ids: number[]) => Promise<void>;
  markAllRead: () => Promise<void>;
  loadNotifications: (page?: number) => Promise<void>;
  loadMore: () => Promise<void>;
}

export function useNotifications(isAuthenticated: boolean): UseNotificationsState {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Poll unread count ───────────────────────────────────────────────────────
  const refreshUnreadCount = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const count = await fetchUnreadCount();
      setUnreadCount(count);
    } catch {
      // best-effort — polling failures are silent
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      setUnreadCount(0);
      return;
    }
    refreshUnreadCount();
    pollRef.current = setInterval(refreshUnreadCount, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [isAuthenticated, refreshUnreadCount]);

  // ── Load / paginate ─────────────────────────────────────────────────────────
  const loadNotifications = useCallback(
    async (page = 1) => {
      if (!isAuthenticated) return;
      setListLoading(true);
      setListError(null);
      try {
        const data: NotificationsPage = await fetchNotifications(page);
        setNotifications((prev) =>
          page === 1 ? data.notifications : [...prev, ...data.notifications],
        );
        setTotal(data.total);
        setCurrentPage(data.page);
        setHasMore(data.hasMore);
        setUnreadCount(data.unreadCount);
      } catch (err) {
        setListError(err instanceof Error ? err.message : 'Failed to load notifications');
      } finally {
        setListLoading(false);
      }
    },
    [isAuthenticated],
  );

  const loadMore = useCallback(() => {
    if (!hasMore || listLoading) return Promise.resolve();
    return loadNotifications(currentPage + 1);
  }, [hasMore, listLoading, currentPage, loadNotifications]);

  // ── Mutations ───────────────────────────────────────────────────────────────
  /**
   * Mark a notification (or group) as read.
   * @param id  - The representative id shown in the UI
   * @param ids - All raw ids in the group (may be length 1 for ungrouped)
   */
  const markRead = useCallback(async (id: number, ids: number[]) => {
    // Optimistic update
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)));
    setUnreadCount((c) => Math.max(0, c - 1));

    try {
      if (ids.length > 1) {
        await apiMarkGroupRead(ids);
      } else {
        await markNotificationRead(id);
      }
    } catch {
      // Rollback
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: false } : n)));
      setUnreadCount((c) => c + 1);
    }
  }, []);

  const markAllRead = useCallback(async () => {
    const prev = notifications;
    const prevCount = unreadCount;
    setNotifications((n) => n.map((item) => ({ ...item, isRead: true })));
    setUnreadCount(0);
    try {
      await markAllNotificationsRead();
    } catch {
      setNotifications(prev);
      setUnreadCount(prevCount);
    }
  }, [notifications, unreadCount]);

  return {
    unreadCount,
    notifications,
    total,
    currentPage,
    hasMore,
    listLoading,
    listError,
    markRead,
    markAllRead,
    loadNotifications,
    loadMore,
  };
}
